import React, { useState } from 'react';
import { Business, AspectCategory } from '../types';
import SocialShare from './SocialShare';
import {
  X,
  Star,
  Camera,
  CheckCircle,
  AlertCircle,
  Heart,
  ChevronDown,
  ChevronUp,
  Share2
} from 'lucide-react';

import { businessAPI, uploadAPI } from '../services/api';
import AlertModal from './AlertModal';
import { POSITIVE_MIN_RATING } from '../config/reviews';


interface ReviewFormProps {
  business: Business;
  onSubmit: (reviewData: any) => Promise<void>;
  onClose: () => void;
}

interface FormData {
  rating: number;
  quickComment: string;
  title: string;
  content: string;
  aspects: { [key in AspectCategory]?: number };
  photos: File[];
  isAnonymous: boolean;
}

interface ValidationErrors {
  rating?: string;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ business, onSubmit, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    rating: 0,
    quickComment: '',
    title: '',
    content: '',
    aspects: {},
    photos: [],
    isAnonymous: false
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDetailedSection, setShowDetailedSection] = useState(false);

  const [imgUpload, setImgUpload] = useState<File[] | []>([]);

  // Alert modal state
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info' as 'success' | 'error' | 'info',
  });

  const [customBusinessName, setCustomBusinessName] = useState('');
  const [customBusinessCategory, setCustomBusinessCategory] = useState('');

  const aspectCategories: { key: AspectCategory; label: string; description: string }[] = [
    { key: 'service', label: 'Service Quality', description: 'How was the customer service?' },
    { key: 'quality', label: 'Product/Service Quality', description: 'How was the overall quality?' },
    { key: 'value', label: 'Value for Money', description: 'Was it worth the price?' },
    { key: 'atmosphere', label: 'Atmosphere', description: 'How was the environment/ambiance?' },
    { key: 'cleanliness', label: 'Cleanliness', description: 'How clean was the establishment?' },
    { key: 'speed', label: 'Speed of Service', description: 'How quick was the service?' }
  ];

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (![1, 3, 5].includes(formData.rating)) {
      newErrors.rating = 'Please select a rating';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // custom business handling
    if (business.id === 'temp') {
      try {
        const { id, ...businessWithoutId } = business;
        businessWithoutId.name = customBusinessName || 'Unnamed Business';
        businessWithoutId.category = customBusinessCategory || 'general';
        // console.log('Creating custom business:', businessWithoutId);
        const customBusiness = await businessAPI.create(businessWithoutId);
        business.id = customBusiness.business.id;
        business.name = customBusiness.business.name;
        business.category = customBusiness.business.category;
        // console.log('Created custom business with ID:', customBusiness.business.id);

      } catch (err) {
        setModal({
          open: true,
          title: 'Sorry!',
          message: 'Failed to save this place. Please try again.',
          variant: 'error',
        });
        // setIsSubmitting(false);
        return;
      }

    }

    setIsSubmitting(true);

    // console.log('Form data to submit:', imgUpload);
    const uploadedUrls: string[] = [];
    for (const file of Array.from(imgUpload)) {

      // console.log("file image: ", file);
      try {
        const { uploadUrl, fileUrl } = await uploadAPI.getReviewPhotoUploadUrl({
          fileName: file.name,
          fileType: file.type,
          reviewId: business.id
        });

        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        await uploadedUrls.push(fileUrl);
        console.log("upload success: ", fileUrl);

      } catch (err) {
        console.error('Photo upload failed:', err);
        // alert(`Failed to upload ${file.name}`);
      }

    }

    // Prepare review data - use quick comment as content if no detailed content
    // const finalContent = formData.content.trim() || formData.quickComment.trim() || 
    const finalContent = formData.content.trim() ||
      `${formData.rating} star${formData.rating !== 1 ? 's' : ''}`;
    const finalTitle = formData.quickComment.trim() ||
      (formData.rating === 5 ? 'Amazing!' :
        formData.rating === 3 ? 'It was good!' : 'Could be better');

    const reviewData = {
      rating: formData.rating,
      title: finalTitle,
      content: finalContent,
      aspects: Object.entries(formData.aspects).map(([category, rating]) => ({
        category,
        rating,
        comment: ''
      })),
      photos: uploadedUrls,
      isAnonymous: formData.isAnonymous
    };

    // console.log('Submitting review data:', reviewData);

    try {
      await onSubmit(reviewData);
      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting review:', error);
      setIsSubmitting(false);
      // alert('Failed to submit review. Please try again.');
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleShareAfterSubmit = () => {
    setShowSuccessModal(false);
    setShowShareModal(true);
  };

  // Render 1/3/5 star rating options
  const ratingOptions = [
    { value: 1, label: 'Could be better', emoji: '🙂', color: 'text-orange-400 border-orange-300 bg-orange-50 hover:bg-orange-100' },
    { value: 3, label: 'It was good', emoji: '😊', color: 'text-blue-500 border-blue-300 bg-blue-50 hover:bg-blue-100' },
    { value: 5, label: 'Amazing!', emoji: '🤩', color: 'text-green-500 border-green-300 bg-green-50 hover:bg-green-100' },
  ];

  const renderStars = (rating: number, onRate?: (rating: number) => void, size = 'w-10 h-10') => {
    return Array.from({ length: 5 }, (_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onRate && onRate(i + 1)}
        className={`${size} ${i < rating
          ? 'text-yellow-400 fill-current'
          : 'text-gray-300 hover:text-yellow-300'
          } transition-colors duration-200 ${onRate ? 'cursor-pointer' : 'cursor-default'}`}
        disabled={!onRate}
      >
        <Star className="w-full h-full" />
      </button>

    ));

  };

  const handlePhotosSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    // console.log("files: ", files);

    const MAX_PHOTOS = 5;
    if (imgUpload.length + files.length > MAX_PHOTOS) {

      setModal({
        open: true,
        title: 'Sorry!',
        message: `You can upload up to ${MAX_PHOTOS} photos.`,
        variant: 'error',
      });
      return;
    }

    for (const file of Array.from(files)) {
      // console.log("file image: ", file);
      setImgUpload(prev => [...prev, file]);
    }

    // console.log("photo: ", formData.photos);
    event.target.value = ''; // allow re-uploading same file
  };




  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className='p-6 border-b border-gray-200'>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Leave a Review</h2>
            <p className="text-sm text-gray-600 mt-1">{business.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>

        </div>


        {business.id === 'temp' && (
          <div className="flex flex-col md:flex-row justify-space-between gap-4 mt-6">
            <div className="flex-1 basis-7/10">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Place name
              </label>
              <input
                type="text"
                className="input-field"
                // value={formData.businessName}
                onChange={(e) => setCustomBusinessName(e.target.value)}
                placeholder="Give this place a name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                className="input-field"
                // value={formData.customCategory}
                onChange={(e) => setCustomBusinessCategory(e.target.value)}
              >
                <option value="">Select a category</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Coffee Shop">Coffee Shop</option>
                <option value="Retail Store">Retail Store</option>
                <option value="Service">Service</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Health & Wellness">Health & Wellness</option>
                <option value="Professional Services">Professional Services</option>
                <option value="Other">Other</option>
                {/* ...more options... */}
              </select>
            </div>
          </div>
        )}
      </div>
      {/* Form Content */}
      <div className="p-6 space-y-6">
        {/* Star Rating - 1 / 3 / 5 options */}
        <div className="text-center space-y-4 pb-6 border-b border-gray-200">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              How was your experience?
            </h3>
            <p className="text-gray-600">Choose your rating</p>
          </div>

          <div className="flex justify-center gap-3">
            {ratingOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, rating: opt.value }))}
                className={`flex flex-col items-center gap-2 px-5 py-4 rounded-xl border-2 transition-all duration-200 font-medium ${
                  formData.rating === opt.value
                    ? opt.color + ' border-current ring-2 ring-offset-1 ring-current shadow-md scale-105'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
                }`}
              >
                <span className="text-3xl">{opt.emoji}</span>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: opt.value }, (_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current text-yellow-400" />
                  ))}
                </div>
                <span className="text-xs whitespace-nowrap">{opt.label}</span>
              </button>
            ))}
          </div>

          {formData.rating > 0 && (
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-800 rounded-full">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-semibold text-lg">
                  {formData.rating === 5 ? 'Amazing!' :
                    formData.rating === 3 ? 'It was good!' : 'Could be better'}
                </span>
              </div>
            </div>
          )}

          {errors.rating && (
            <div className="flex items-center justify-center space-x-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">{errors.rating}</span>
            </div>
          )}
        </div>

        {/* Quick Comment (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick comment <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={formData.quickComment}
            onChange={(e) => setFormData(prev => ({ ...prev, quickComment: e.target.value }))}
            className="input-field h-20 resize-none"
            placeholder="Share a quick thought about your experience..."
            maxLength={200}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {formData.quickComment.length}/200
          </div>
        </div>

        {/* Collapsible Detailed Section */}
        <div className="border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setShowDetailedSection(!showDetailedSection)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-200"
          >
            <span className="font-medium text-gray-900">
              Add more details <span className="text-gray-400">(optional)</span>
            </span>
            {showDetailedSection ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showDetailedSection && (
            <div className="p-4 pt-0 space-y-4 border-t border-gray-200">

              {/* Detailed Content */}
              <div>
                <label className="block pt-4 text-sm font-medium text-gray-700 mb-1">
                  Detailed review <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="input-field h-32 resize-none"
                  placeholder="Tell us more about what made your experience special..."
                  maxLength={1000}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.content.length}/1000
                </div>
              </div>

              {/* Aspect Ratings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Rate specific aspects <span className="text-gray-400">(optional)</span>
                </label>
                <div className="space-y-3">
                  {aspectCategories.map((aspect) => (
                    <div key={aspect.key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{aspect.label}</span>
                      <div className="flex space-x-1">
                        {renderStars(
                          formData.aspects[aspect.key] || 0,
                          (rating) => setFormData(prev => ({
                            ...prev,
                            aspects: { ...prev.aspects, [aspect.key]: rating }
                          })),
                          'w-6 h-6'
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors duration-200">
                <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Add up to 5 photos</p>
                <label className="text-primary-600 hover:text-primary-700 font-medium text-sm cursor-pointer">
                  Choose Files
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotosSelected}
                  />
                </label>
                {imgUpload.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {imgUpload.map((file, idx) => {
                      const previewUrl = URL.createObjectURL(file);

                      return (
                        <div key={`${file.name}-${file.lastModified}-${idx}`} className="relative text-center">
                          <img
                            src={previewUrl}
                            alt={file.name}
                            className="w-16 h-16 object-cover rounded"
                            onLoad={() => URL.revokeObjectURL(previewUrl)}
                          />
                          <p className="mt-1 text-xs text-gray-600 truncate w-16">{file.name}</p>

                          <button
                            type="button"
                            className="absolute -top-2 -right-2 bg-white border rounded-full text-gray-700 w-5 h-5 flex items-center justify-center shadow"
                            onClick={() =>
                              setImgUpload(prev => prev.filter((_, fileIdx) => fileIdx !== idx))
                            }
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>




            </div>
          )}
        </div>

        {/* Privacy Option */}
        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="anonymous"
            checked={formData.isAnonymous}
            onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <div>
            <label htmlFor="anonymous" className="font-medium text-gray-900 cursor-pointer text-sm">
              Post anonymously
            </label>
            <p className="text-xs text-gray-600 mt-0.5">
              Your name won't be shown publicly
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="p-6 border-t border-gray-200">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || ![1, 3, 5].includes(formData.rating)}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 py-4 text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Heart className="w-5 h-5" />
              <span>Submit Review</span>
            </>
          )}
        </button>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Review Submitted Successfully!
            </h3>

            <p className="text-gray-600 mb-6">
              {formData.rating >= POSITIVE_MIN_RATING ? (
                <>
                  Thank you for sharing your positive experience at {business.name}.
                  Your review helps others discover great businesses!
                </>
              ) : (
                <>
                  Thanks for your honest feedback on {business.name}.
                  Sharing what could be better helps businesses improve and helps others decide.
                </>
              )}
            </p>

            <div className="space-y-3">
              {formData.rating >= POSITIVE_MIN_RATING && (
                <button
                  onClick={handleShareAfterSubmit}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share Your Review</span>
                </button>
              )}

              <button
                onClick={handleSuccessClose}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Continue Exploring
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Share Modal */}
      {showShareModal && (
        <SocialShare
          business={business}
          review={{
            id: 'new-review',
            businessId: business.id,
            userId: 'current-user',
            userName: formData.isAnonymous ? 'Anonymous User' : 'Your Name',
            rating: formData.rating,
            title: formData.title,
            content: formData.content,
            aspects: Object.entries(formData.aspects).map(([category, rating]) => ({
              category: category as AspectCategory,
              rating
            })),
            createdAt: new Date(),
            updatedAt: new Date(),
            helpful: 0
          }}
          type="review"
          onClose={() => {
            setShowShareModal(false);
            onClose();
          }}
        />
      )}

      <AlertModal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        variant={modal.variant}
        onClose={() => setModal(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default ReviewForm;
